import type { SelectOption } from '@movingimage-evp/mi-ui-component-library';
import type { ChangeEvent } from 'react';

import type { GetWebcastIngestStateQuery, Language } from '../../../../generated/graphql-manager';

import {
  CopyToClipboard,
  Heading,
  InputDescription,
  Note,
  Paragraph,
  PlusIcon,
  PrimaryButton,
  Select,
  SelectionInputLayout,
  TextInput,
  Toggle,
  notification,
  toast,
} from '@movingimage-evp/mi-ui-component-library';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useCurrentUser } from 'src/manager/hooks/current-user';

import { DictionaryWordsList } from './dictionary-words-list/dictionary-words-list';
import {
  type DictionaryWord,
  DictionaryWordsModal,
  MAX_DICTIONARY_ENTRIES,
  MAX_DICTIONARY_ENTRY_LENGTH,
} from './dictionary-words-modal/dictionary-words-modal';
import { IngestActiveModal } from './ingest-active-modal';
import {
  ItemListAction,
  State,
  StreamState,
  useGetWebcastIngestStateLazyQuery,
  useGetWebcastLanguagesQuery,
  useUpdateWebcastLanguagesMutation,
} from '../../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../../routes';
import { getAiTranslationKey, getLanguageKey } from '../../../../utils';
import { SetupPageFooter } from '../../../components/setup-page-footer';
import { useGetFeatures } from '../../../hooks/use-get-features';
import { useUserPermissions } from '../../../hooks/user-permissions';

import styles from './language.module.css';
import managerStyles from '../../../manager.module.css';

type SaveChangesOptions = {
  primaryLanguage?: Language;
  additionalLanguages?: Language[];
  aiLiveTranscriptGeneration?: boolean;
  aiLiveTranscriptEditorEnabled?: boolean;
  translationLanguages?: string[];
};

const isAiSubtitleChange = (options?: SaveChangesOptions) =>
  options?.aiLiveTranscriptGeneration !== undefined ||
  options?.aiLiveTranscriptEditorEnabled !== undefined ||
  options?.translationLanguages !== undefined;

const isIngestConnected = (data?: GetWebcastIngestStateQuery) =>
  data?.webcast?.streaming?.live?.streams?.some(
    (stream) =>
      stream.primaryStatus.streamState === StreamState.CONNECTED ||
      stream.backupStatus.streamState === StreamState.CONNECTED
  ) ?? false;

export function LanguagePage() {
  const { webcastId = '' } = useParams();
  const { lsproId } = useCurrentUser();
  const { t } = useTranslation();
  const routes = useAbsoluteRoutes();
  const { isEventEditingAllowed } = useUserPermissions();
  const { isAiSubtitleFeatureEnabled } = useGetFeatures();

  const hasInitializedRef = useRef(false);

  const { data } = useGetWebcastLanguagesQuery({
    variables: { webcastId, lsproId },
    onCompleted({ webcast, lsproSettings, configuration }) {
      if (hasInitializedRef.current) return;
      hasInitializedRef.current = true;

      const additionalLangsCodes = lsproSettings?.languages.map((language) => language.code) || [];
      const languagesToUse = Array.from(new Set([...configuration.supportedLanguages, ...additionalLangsCodes]));

      setLanguagesData(
        languagesToUse.map((supportedLanguage) => ({
          language: supportedLanguage,
          title: webcast.contents.find(({ language }) => language === supportedLanguage)?.title || '',
        }))
      );

      const glossaryResult = webcast.subtitleSettings?.glossary;
      const glossaryEntries = glossaryResult?.__typename === 'GlossaryList' ? glossaryResult.entries : [];

      setDictionaryWords(
        glossaryEntries.map((entry) => ({ word: entry.term, soundsLike: entry.alternatePronunciations }))
      );
    },
  });

  const webcast = data?.webcast;
  const allChangesPossible = webcast?.state === State.PRELIVE;

  const supportedSubtitleLanguages = data?.configuration.supportedSubtitleLanguages || [];

  const [languagesData, setLanguagesData] = useState<{ language: string; title: string }[]>([]);
  const [primaryTitleMissingError, setPrimaryTitleMissingError] = useState(false);
  const [isDictionaryModalOpen, setIsDictionaryModalOpen] = useState(false);
  const [dictionaryWords, setDictionaryWords] = useState<DictionaryWord[]>([]);
  const [editingWordIndex, setEditingWordIndex] = useState<number>();
  const [isIngestModalOpen, setIsIngestModalOpen] = useState(false);

  const pendingChangeRef = useRef<SaveChangesOptions | undefined>(undefined);
  // Bumped on every passesIngestCheck call so concurrent invocations can
  // detect that they've been superseded and bail without side effects.
  const ingestRequestIdRef = useRef(0);

  // Optimistic overlay applied while we wait for the ingest-state check, so the UI
  // reflects the user's intent immediately. Cleared once the mutation takes over
  // (its own optimisticResponse keeps the cache aligned) or when we revert.
  const [optimisticChange, setOptimisticChange] = useState<SaveChangesOptions | undefined>();

  const primaryLanguage = optimisticChange?.primaryLanguage ?? webcast?.primaryLang;
  const aiLiveTranscriptGeneration =
    optimisticChange?.aiLiveTranscriptGeneration ?? webcast?.subtitleSettings?.aiLiveTranscriptGeneration === true;
  const aiLiveTranscriptEditorEnabled =
    optimisticChange?.aiLiveTranscriptEditorEnabled ??
    webcast?.subtitleSettings?.aiLiveTranscriptEditorEnabled === true;
  const translationLanguages =
    optimisticChange?.translationLanguages ?? webcast?.subtitleSettings?.translationLanguages ?? [];
  const glossary = webcast?.subtitleSettings?.glossary ?? {
    __typename: 'GlossaryList' as const,
    entries: [],
  };

  const languages = data?.lsproSettings?.languages || [];
  const additionalLanguages =
    optimisticChange?.additionalLanguages ??
    webcast?.additionalLangs.filter((language) => language.code !== primaryLanguage?.code) ??
    [];

  const [fetchIngestState, { loading: isCheckingIngest }] = useGetWebcastIngestStateLazyQuery({
    variables: { webcastId },
    fetchPolicy: 'network-only',
  });

  const [updateWebcastLanguagesMutation] = useUpdateWebcastLanguagesMutation();

  const onTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;

    setLanguagesData((state) =>
      state.map((language) => (language.language === name ? { ...language, title: value } : language))
    );
  };

  // Returns true if the change can proceed to the mutation, false if it was
  // blocked (modal opened), the ingest-state query failed, or this call was
  // superseded by a later one.
  const passesIngestCheck = async (options: SaveChangesOptions, updatedAdditionalLanguages: Language[]) => {
    const requestId = ++ingestRequestIdRef.current;
    const isLatest = () => requestId === ingestRequestIdRef.current;

    pendingChangeRef.current = options;
    const optimisticApplied = !isIngestModalOpen;

    if (optimisticApplied) {
      setOptimisticChange({ ...options, additionalLanguages: updatedAdditionalLanguages });
    }

    const revertOptimistic = () => {
      if (optimisticApplied) setOptimisticChange(undefined);
    };

    let ingestResult;

    try {
      ingestResult = await fetchIngestState();
    } catch {
      if (!isLatest()) return false;
      revertOptimistic();
      pendingChangeRef.current = undefined;
      notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      return false;
    }

    if (!isLatest()) return false;

    if (isIngestConnected(ingestResult.data)) {
      revertOptimistic();
      setIsIngestModalOpen(true);
      return false;
    }

    setIsIngestModalOpen(false);
    pendingChangeRef.current = undefined;
    return true;
  };

  const saveChanges = async (options?: SaveChangesOptions) => {
    const newPrimaryLanguage = options?.primaryLanguage || primaryLanguage;

    if (!newPrimaryLanguage || !primaryLanguage) return;

    const updatedAdditionalLanguages = updateAdditionalLanguages(
      primaryLanguage,
      newPrimaryLanguage,
      options?.additionalLanguages || additionalLanguages
    );
    const updatedLanguagesData = updateLanguagesData(primaryLanguage, newPrimaryLanguage);

    setLanguagesData(updatedLanguagesData);

    if (isAiSubtitleChange(options) && !(await passesIngestCheck(options!, updatedAdditionalLanguages))) return;

    const languages = [
      { language: newPrimaryLanguage.code, isPrimary: true },
      ...(updatedAdditionalLanguages || []).map((language) => ({ language: language.code, isPrimary: false })),
    ];
    const contents = updatedLanguagesData?.filter(
      ({ language }) => languages.find((updatedLanguage) => updatedLanguage.language === language) !== undefined
    );
    const newSubtitleSettings = {
      aiLiveTranscriptGeneration: options?.aiLiveTranscriptGeneration ?? aiLiveTranscriptGeneration,
      aiLiveTranscriptEditorEnabled: options?.aiLiveTranscriptEditorEnabled ?? aiLiveTranscriptEditorEnabled,
      translationLanguages: options?.translationLanguages ?? translationLanguages,
    };

    updateWebcastLanguagesMutation({
      variables: {
        input: {
          id: webcastId,
          languages: !allChangesPossible ? undefined : languages,
          subtitleSettings: !allChangesPossible ? undefined : newSubtitleSettings,
          contents,
        },
      },
      optimisticResponse: {
        updateWebcast: {
          __typename: 'UpdateWebcastSuccess',
          webcast: {
            __typename: 'Webcast',
            id: webcastId,
            primaryLang: newPrimaryLanguage,
            additionalLangs: updatedAdditionalLanguages,
            subtitleSettings: { __typename: 'SubtitleSettings', ...newSubtitleSettings, glossary },
            contents,
          },
        },
      },
      update: (cache, { data }) => {
        if (data?.updateWebcast.__typename !== 'UpdateWebcastSuccess') return;

        cache.modify({
          id: cache.identify({ __typename: 'Webcast', id: webcastId }),
          fields: {
            primaryLang: () => newPrimaryLanguage,
            additionalLangs: () => updatedAdditionalLanguages,
            subtitleSettings: () => (allChangesPossible ? { ...newSubtitleSettings, glossary } : undefined),
            contents: () => contents,
          },
        });
      },
      onCompleted: (data) => {
        setOptimisticChange(undefined);
        if (data?.updateWebcast.__typename === 'UpdateWebcastSuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        setOptimisticChange(undefined);
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const saveGlossaryChanges = (action: ItemListAction, entries: DictionaryWord[], updatedWords: DictionaryWord[]) => {
    if (!primaryLanguage) return;

    const previousWords = [...dictionaryWords];

    const glossaryEntries = entries.map((entry) => ({
      term: entry.word,
      alternatePronunciations: action === ItemListAction.REMOVE ? [] : entry.soundsLike,
    }));

    const optimisticGlossary = updatedWords.map((entry) => ({
      __typename: 'SubtitleGlossaryEntry' as const,
      term: entry.word,
      alternatePronunciations: entry.soundsLike,
    }));

    return updateWebcastLanguagesMutation({
      variables: {
        input: {
          id: webcastId,
          subtitleSettings: {
            glossary: {
              action,
              entries: glossaryEntries,
            },
          },
        },
      },
      optimisticResponse: {
        updateWebcast: {
          __typename: 'UpdateWebcastSuccess',
          webcast: {
            __typename: 'Webcast',
            id: webcastId,
            primaryLang: primaryLanguage,
            additionalLangs: additionalLanguages,
            subtitleSettings: {
              __typename: 'SubtitleSettings',
              aiLiveTranscriptGeneration,
              aiLiveTranscriptEditorEnabled,
              translationLanguages,
              glossary: { __typename: 'GlossaryList', entries: optimisticGlossary },
            },
            contents: webcast?.contents || [],
          },
        },
      },
      update: (cache, { data }) => {
        const response = data?.updateWebcast;
        if (response?.__typename !== 'UpdateWebcastSuccess') return;

        cache.modify({
          id: cache.identify({ __typename: 'Webcast', id: webcastId }),
          fields: {
            subtitleSettings() {
              return {
                aiLiveTranscriptGeneration,
                aiLiveTranscriptEditorEnabled,
                translationLanguages,
                glossary: { __typename: 'GlossaryList', entries: optimisticGlossary },
              };
            },
          },
        });
      },
      onCompleted: (data) => {
        if (data?.updateWebcast.__typename === 'UpdateWebcastSuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          setDictionaryWords(previousWords);
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        setDictionaryWords(previousWords);
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const getPrimaryLanguageTitle = (primaryLanguage?: Language) => {
    return languagesData?.find((language) => language.language === primaryLanguage?.code)?.title || '';
  };

  const handleOnPrimaryTitleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (primaryLanguage && !getPrimaryLanguageTitle(primaryLanguage)) {
      setPrimaryTitleMissingError(true);
    } else {
      setPrimaryTitleMissingError(false);
      handleOnLanguageTitleBlur(event);
    }
  };

  const handleOnLanguageTitleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;

    const title = webcast?.contents?.find((language) => language.language === name)?.title || '';
    const updatedTitle = languagesData?.find((language) => language.language === name)?.title || '';

    if (title !== updatedTitle) saveChanges();
  };

  const updateAdditionalLanguages = (
    currentPrimaryLanguage: Language,
    newPrimaryLanguage: Language,
    additionalLanguages: Language[]
  ) => {
    if (!currentPrimaryLanguage) return additionalLanguages;

    const hasSwitchedLanguageBeenChecked = additionalLanguages.some(
      (language) => language.code === newPrimaryLanguage.code
    );

    const updatedAdditionalLanguages = (
      hasSwitchedLanguageBeenChecked ? [...additionalLanguages, currentPrimaryLanguage] : additionalLanguages
    ).filter((language) => language.code !== newPrimaryLanguage.code);

    return updatedAdditionalLanguages;
  };

  const updateLanguagesData = (currentPrimaryLanguage: Language, newPrimaryLanguage: Language) => {
    const currentPrimaryLanguageTitle = getPrimaryLanguageTitle(currentPrimaryLanguage);
    const newPrimaryLanguageTitle = getPrimaryLanguageTitle(newPrimaryLanguage);

    const newSelectedPrimaryLanguageHasNoTitle = newPrimaryLanguageTitle === '';

    const updatedLanguagesData = newSelectedPrimaryLanguageHasNoTitle
      ? languagesData?.map((language) => ({
          ...language,
          title: language.language === newPrimaryLanguage.code ? currentPrimaryLanguageTitle : language.title,
        }))
      : languagesData;

    return updatedLanguagesData;
  };

  const supportedSubtitleLanguagesWithoutEventLanguages = supportedSubtitleLanguages.filter(
    (subtitleLanguage) =>
      subtitleLanguage !== primaryLanguage?.code &&
      !additionalLanguages.some((language) => language.code?.startsWith(subtitleLanguage))
  );

  const primaryLanguageOptions = languages
    .filter((language) => language.__typename === 'StandardLanguage')
    .map((language) => ({
      key: language.code,
      label: t(`manager.webcastSetup.language.supportedLanguages.${getLanguageKey(language.code)}`),
    }));

  const primaryLanguageValue = primaryLanguageOptions.find((option) => option.key === primaryLanguage?.code);
  const primaryLanguageTitle = getPrimaryLanguageTitle(primaryLanguage);

  const onPrimaryLanguageChange = (updatedPrimaryLanguage?: Language) => {
    if (!updatedPrimaryLanguage) return;

    saveChanges({ primaryLanguage: updatedPrimaryLanguage });
  };

  const additionalLanguagesOptions = languages
    .filter((language) => language.code !== primaryLanguage?.code)
    .map((language) => ({
      key: language.code,
      label:
        language.__typename === 'CustomLanguage'
          ? language.name
          : t(`manager.webcastSetup.language.supportedLanguages.${getLanguageKey(language.code)}`),
    }));

  const additionalLanguagesValue = additionalLanguagesOptions.filter((option) =>
    additionalLanguages.some((language) => language.code === option.key)
  );

  const aiSubtitlesOptions = supportedSubtitleLanguagesWithoutEventLanguages
    .map((language) => ({
      key: language,
      label: t(`common.languages.${getAiTranslationKey(language)}`),
    }))
    .sort((a, b) => (a.label > b.label ? 1 : -1));

  const isMultiLanguageEvent = additionalLanguages.length > 0;

  const languageSelectionDisabled = !isEventEditingAllowed || webcast?.state !== State.PRELIVE;
  const aiSubtitlesDisabled =
    languageSelectionDisabled || webcast?.subtitleSettings?.aiLiveTranscriptGeneration === undefined;

  if (!primaryLanguage) return null;

  return (
    <main className={managerStyles.main} data-testid="language-page">
      <Heading className={managerStyles.grayText}>{t('manager.webcastSetup.language.title')}</Heading>
      <Paragraph>{t('manager.webcastSetup.language.headings.primaryLanguage')}</Paragraph>

      <div className={styles.wrapper}>
        <InputDescription
          label={t('manager.webcastSetup.language.inputs.primaryLanguage.label')}
          disabled={languageSelectionDisabled}
        >
          <Select
            data-testid="primary-language-select"
            disabled={languageSelectionDisabled}
            optionsIdProperty="key"
            value={primaryLanguageValue}
            options={primaryLanguageOptions}
            onChange={(option: SelectOption) => {
              onPrimaryLanguageChange(languages.find((language) => language.code === option.key));
            }}
          />
        </InputDescription>

        <InputDescription
          label={t('manager.webcastSetup.language.inputs.additionalLanguages.label')}
          disabled={languageSelectionDisabled}
        >
          <Select
            data-testid="additional-languages"
            disabled={languageSelectionDisabled}
            placeholder={t('manager.webcastSetup.language.inputs.additionalLanguages.placeholder')}
            optionsIdProperty="key"
            value={additionalLanguagesValue}
            options={additionalLanguagesOptions}
            onChange={(options) => {
              saveChanges({
                additionalLanguages: languages.filter((language) =>
                  options.some((option: SelectOption) => option.key === language.code)
                ),
              });
            }}
          />
        </InputDescription>

        {isMultiLanguageEvent && (
          <Paragraph>{t('manager.webcastSetup.language.headings.additionalLanguages')}</Paragraph>
        )}

        <InputDescription
          inputId="primary-language-title"
          label={
            isMultiLanguageEvent
              ? t('manager.webcastSetup.language.inputs.primaryLanguageTitle.multiLanguageLabel')
              : t('manager.webcastSetup.language.inputs.primaryLanguageTitle.label')
          }
          helpMessage={
            !isMultiLanguageEvent
              ? t('manager.webcastSetup.language.inputs.primaryLanguageTitle.helpMessage')
              : undefined
          }
          disabled={!isEventEditingAllowed}
          errorMessage={
            primaryTitleMissingError
              ? t('manager.webcastSetup.language.inputs.primaryLanguageTitle.primaryTitleMissing')
              : undefined
          }
        >
          <TextInput
            value={primaryLanguageTitle}
            name={primaryLanguage.code}
            disabled={!isEventEditingAllowed}
            onChange={onTitleChange}
            onBlur={handleOnPrimaryTitleBlur}
            data-testid="primary-language-title"
          >
            <CopyToClipboard value={primaryLanguageTitle} notificationPosition="left" />
          </TextInput>
        </InputDescription>

        {languagesData
          .filter(
            ({ language }) =>
              language !== primaryLanguage.code && additionalLanguages.find(({ code }) => code === language)
          )
          .map(({ language, title }) => {
            const foundLanguage = additionalLanguages.find(({ code }) => code === language);
            const customLabel = foundLanguage?.__typename === 'CustomLanguage' ? foundLanguage.name : undefined;

            return (
              <InputDescription
                key={language}
                inputId={`language-title-${language}`}
                label={customLabel || t(`common.languages.${getLanguageKey(language)}`)}
                disabled={!isEventEditingAllowed}
              >
                <TextInput
                  data-testid={`language-title-${language}`}
                  name={language}
                  value={title || ''}
                  disabled={!isEventEditingAllowed}
                  onChange={onTitleChange}
                  onBlur={handleOnLanguageTitleBlur}
                />
              </InputDescription>
            );
          })}

        {isAiSubtitleFeatureEnabled && (
          <div className={styles.wrapper}>
            <div data-testid="ai-subtitles-toggle-wrapper">
              <SelectionInputLayout
                hintMessage={t('manager.webcastSetup.language.inputs.aiSubtitles.hintMessage')}
                checked={aiLiveTranscriptGeneration}
                disabled={aiSubtitlesDisabled}
                variant="ghost"
                inputElement={
                  <Toggle
                    data-testid="ai-subtitles-toggle"
                    checked={aiLiveTranscriptGeneration}
                    disabled={aiSubtitlesDisabled}
                    onChange={() => {
                      saveChanges({ aiLiveTranscriptGeneration: !aiLiveTranscriptGeneration });
                    }}
                  >
                    {t('manager.webcastSetup.language.inputs.aiSubtitles.label')}
                  </Toggle>
                }
              />
            </div>

            {aiLiveTranscriptGeneration && (
              <>
                <div data-testid="ingest-block-subtitle-languages">
                  <InputDescription label={t('manager.webcastSetup.language.inputs.aiSubtitlesLanguages.label')}>
                    <Select
                      data-testid="ai-subtitles-languages"
                      disabled={aiSubtitlesDisabled}
                      placeholder={t('manager.webcastSetup.language.inputs.aiSubtitlesLanguages.placeholder')}
                      optionsIdProperty="key"
                      value={translationLanguages.map((language) => ({
                        key: language,
                        label: t(`common.languages.${getAiTranslationKey(language)}`),
                      }))}
                      options={aiSubtitlesOptions}
                      onChange={(options) => {
                        saveChanges({ translationLanguages: options.map((option: SelectOption) => option.key) });
                      }}
                    />
                  </InputDescription>
                </div>

                <div className={styles.customDictionarySection}>
                  <header>
                    <span>{t('manager.webcastSetup.language.inputs.customDictionary.toggle.label')}</span>
                    <span>{t('manager.webcastSetup.language.inputs.customDictionary.toggle.hintMessage')}</span>
                  </header>

                  <PrimaryButton
                    small
                    disabled={aiSubtitlesDisabled}
                    data-testid="add-dictionary-words-button"
                    onClick={() => {
                      setEditingWordIndex(undefined);
                      setIsDictionaryModalOpen(true);
                    }}
                  >
                    {t('manager.webcastSetup.language.inputs.customDictionary.addWordsButton')}
                    <PlusIcon />
                  </PrimaryButton>

                  {!aiSubtitlesDisabled && (
                    <DictionaryWordsList
                      dictionaryWords={dictionaryWords}
                      onEdit={(index) => {
                        setEditingWordIndex(index);
                        setIsDictionaryModalOpen(true);
                      }}
                      onRemove={(index) => {
                        const wordToRemove = dictionaryWords[index];
                        const updatedWords = dictionaryWords.filter((_, i) => i !== index);
                        setDictionaryWords(updatedWords);
                        saveGlossaryChanges(ItemListAction.REMOVE, [wordToRemove], updatedWords);
                      }}
                      onRemoveAll={async () => {
                        const wordsToRemove = [...dictionaryWords];
                        setDictionaryWords([]);

                        if (wordsToRemove.length <= MAX_DICTIONARY_ENTRIES) {
                          saveGlossaryChanges(ItemListAction.REMOVE, wordsToRemove, []);
                          return;
                        }

                        let remainingWords = [...wordsToRemove];

                        for (let i = 0; i < wordsToRemove.length; i += MAX_DICTIONARY_ENTRIES) {
                          const batch = wordsToRemove.slice(i, i + MAX_DICTIONARY_ENTRIES);
                          const glossaryEntries = batch.map((entry) => ({
                            term: entry.word,
                            alternatePronunciations: [] as string[],
                          }));

                          try {
                            const result = await updateWebcastLanguagesMutation({
                              variables: {
                                input: {
                                  id: webcastId,
                                  subtitleSettings: {
                                    glossary: { action: ItemListAction.REMOVE, entries: glossaryEntries },
                                  },
                                },
                              },
                            });

                            if (result.data?.updateWebcast.__typename !== 'UpdateWebcastSuccess') {
                              setDictionaryWords(remainingWords);
                              notification(t('manager.webcastSetup.toast.saveError'), 'negative');
                              return;
                            }

                            remainingWords = remainingWords.filter((w) => !batch.some((b) => b.word === w.word));
                          } catch {
                            setDictionaryWords(remainingWords);
                            notification(t('manager.webcastSetup.toast.saveError'), 'negative');
                            return;
                          }
                        }

                        toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
                      }}
                    />
                  )}
                </div>

                <div data-testid="ai-subtitles-editor-wrapper">
                  <SelectionInputLayout
                    hintMessage={t('manager.webcastSetup.language.inputs.aiLiveTranscriptEditor.toggle.hintMessage')}
                    checked={aiLiveTranscriptEditorEnabled}
                    variant="ghost"
                    inputElement={
                      <Toggle
                        data-testid="ai-subtitles-editor-toggle"
                        checked={aiLiveTranscriptEditorEnabled}
                        disabled={aiSubtitlesDisabled}
                        onChange={() => {
                          saveChanges({ aiLiveTranscriptEditorEnabled: !aiLiveTranscriptEditorEnabled });
                        }}
                      >
                        {t('manager.webcastSetup.language.inputs.aiLiveTranscriptEditor.toggle.label')}
                      </Toggle>
                    }
                  />
                </div>

                <Note variant="primary" className={styles.note}>
                  <strong>{t('common.aiSubtitlesDelayInfo.title')}</strong>
                  {t('common.aiSubtitlesDelayInfo.full')}
                </Note>
              </>
            )}
          </div>
        )}
      </div>

      <SetupPageFooter
        nextLabel={t('manager.webcastSetup.language.nextStep.label')}
        route={routes.webcastSetup_eventDescription}
      />

      <DictionaryWordsModal
        isOpen={isDictionaryModalOpen}
        initialWord={editingWordIndex !== undefined ? dictionaryWords[editingWordIndex] : undefined}
        existingWords={dictionaryWords}
        onClose={() => {
          setIsDictionaryModalOpen(false);
          setEditingWordIndex(undefined);
        }}
        onConfirm={async (words: DictionaryWord[]) => {
          if (editingWordIndex !== undefined) {
            const tooLongEntry = words.find(
              (entry) =>
                entry.word.length > MAX_DICTIONARY_ENTRY_LENGTH ||
                entry.soundsLike.some((s) => s.length > MAX_DICTIONARY_ENTRY_LENGTH)
            );

            if (tooLongEntry) {
              notification(
                t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.maxCharacters', {
                  word: `${tooLongEntry.word.slice(0, 30)}…`,
                }),
                'negative'
              );

              return false;
            }

            const oldWord = dictionaryWords[editingWordIndex];
            const updatedWords = dictionaryWords.map((word, i) => (i === editingWordIndex ? words[0] : word));
            setDictionaryWords(updatedWords);

            try {
              const removeResult = await saveGlossaryChanges(ItemListAction.REMOVE, [oldWord], updatedWords);
              if (removeResult?.data?.updateWebcast.__typename !== 'UpdateWebcastSuccess') return;
            } catch {
              return;
            }

            saveGlossaryChanges(ItemListAction.ADD, words, updatedWords);
          } else {
            const tooLongEntry = words.find(
              (entry) =>
                entry.word.length > MAX_DICTIONARY_ENTRY_LENGTH ||
                entry.soundsLike.some((s) => s.length > MAX_DICTIONARY_ENTRY_LENGTH)
            );

            if (tooLongEntry) {
              notification(
                t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.maxCharacters', {
                  word: `${tooLongEntry.word.slice(0, 30)}…`,
                }),
                'negative'
              );

              return false;
            }

            if (words.length > MAX_DICTIONARY_ENTRIES) {
              notification(
                t('manager.webcastSetup.language.inputs.customDictionary.modal.maxSizeError', {
                  max: MAX_DICTIONARY_ENTRIES,
                }),
                'negative'
              );

              return false;
            }

            const updatedWords = [...dictionaryWords, ...words];
            setDictionaryWords(updatedWords);
            saveGlossaryChanges(ItemListAction.ADD, words, updatedWords);
          }

          setEditingWordIndex(undefined);
        }}
      />

      <IngestActiveModal
        isOpen={isIngestModalOpen}
        isLoading={isCheckingIngest}
        onClose={() => {
          if (isCheckingIngest) return;
          pendingChangeRef.current = undefined;
          setIsIngestModalOpen(false);
        }}
        onTryAgain={() => {
          saveChanges(pendingChangeRef.current);
        }}
      />
    </main>
  );
}
